import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// Mock API call to fetch user role (Replace with actual API call)
const fetchUserData = async () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        email: "test@example.com",
        role: "user",
      });
    }, 100);
  });
};

const SettingsPage = () => {
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([
    { id: 1, email: "member1@example.com" },
    { id: 2, email: "member2@example.com" },
  ]);
  const [newMemberEmail, setNewMemberEmail] = useState("");

  useEffect(() => {
    const getUserData = async () => {
      const userData = await fetchUserData();
      if (userData.email === "test@example.com") {
        userData.role = "admin";
      }
      setUser(userData);
      setLoading(false);
    };

    getUserData();
  }, []);

  const handlePlanChange = (event) => {
    setSelectedPlan(event.target.value);
  };

  const handleSave = () => {
    alert(`Plan updated to: ${selectedPlan}`);
  };

  const handleAddMember = () => {
    if (newMemberEmail.trim() === "") {
      alert("Please enter an email address.");
      return;
    }

    const newMember = {
      id: teamMembers.length + 1,
      email: newMemberEmail,
    };
    setTeamMembers([...teamMembers, newMember]);
    setNewMemberEmail("");
  };

  const handleDeleteMember = (id) => {
    setTeamMembers(teamMembers.filter((member) => member.id !== id));
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box sx={{ flexGrow: 1, padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>

        {user.role === "admin" && (
          <>
            <Paper elevation={3} sx={{ padding: 3, marginBottom: 4 }}>
              <Typography variant="h6" gutterBottom>
                Select a Plan
              </Typography>
              <RadioGroup value={selectedPlan} onChange={handlePlanChange}>
                <FormControlLabel
                  value="starter"
                  control={<Radio />}
                  label="Starter Plan - $4.99/month"
                />
                <FormControlLabel
                  value="pro"
                  control={<Radio />}
                  label="Growth Plan - $14.99/month"
                />
                <FormControlLabel
                  value="enterprise"
                  control={<Radio />}
                  label="Enterprise Plan - $49.99/month"
                />
              </RadioGroup>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{ marginTop: 2 }}
              >
                Save Changes
              </Button>
            </Paper>

            <Paper elevation={3} sx={{ padding: 3, marginBottom: 4 }}>
              <Typography variant="h6" gutterBottom>
                Team Management
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.email}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteMember(member.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder="Enter email to invite"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddMember}
                      >
                        Invite
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {(user.role === "admin" || user.role === "billing_admin") && (
          <Paper elevation={3} sx={{ padding: 3, marginBottom: 4 }}>
            <Typography variant="h6" gutterBottom>
              Billing Information
            </Typography>
            <Typography variant="body1">
              Payment Method: Visa ending in 1234
            </Typography>
            <Typography variant="body1">
              Next Billing Date: Jan 15, 2025
            </Typography>
            <Button variant="outlined" sx={{ marginTop: 2 }}>
              Update Payment Method
            </Button>
          </Paper>
        )}

        <Paper elevation={3} sx={{ padding: 3 }}>
          <Typography variant="h6" gutterBottom>
            Compare Plans
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell align="center">Starter Plan</TableCell>
                <TableCell align="center">Basic Plan</TableCell>
                <TableCell align="center">Pro Plan</TableCell>
                <TableCell align="center">Enterprise Plan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Integrations</TableCell>
                <TableCell align="center">1</TableCell>
                <TableCell align="center">2</TableCell>
                <TableCell align="center">3</TableCell>
                <TableCell align="center">Unlimited</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Data History</TableCell>
                <TableCell align="center">1 week</TableCell>
                <TableCell align="center">1 month</TableCell>
                <TableCell align="center">6 months</TableCell>
                <TableCell align="center">Unlimited</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Actionable Insights</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">✅</TableCell>
                <TableCell align="center">✅</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Export Options</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">CSV</TableCell>
                <TableCell align="center">CSV, Excel, API</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Priority Support</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">✅</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Team Collaboration</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">✅</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Custom Dashboards</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">✅</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default SettingsPage;
